/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { verify } from '../../ducks/EmailVerification.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { parse } from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import {
    NamedRedirect,
    NamedLink,
} from '../../components';

import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import Footer from '../../custom-components/elements/footer/Footer';

import css from './EmailVerificationPage.css';

// Purple lockup (dark text on white) used specifically on this card — coaches.
const VERIFICATION_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 48" width="180" height="48"><defs><radialGradient id="vlg2" cx="78%" cy="22%" r="65%"><stop offset="0%" stop-color="#C084FC"/><stop offset="50%" stop-color="#9333EA"/><stop offset="100%" stop-color="#581C87"/></radialGradient></defs><svg x="4" y="4" width="40" height="40" viewBox="0 0 120 120"><circle cx="60" cy="60" r="56" fill="url(#vlg2)"/><path d="M26,26 L43,30 L58,78 L77,30 L96,24 L74,99 L48,99 Z" fill="white"/></svg><text x="56" y="32" font-family="Plus Jakarta Sans,sans-serif" font-weight="800" font-size="22.4" letter-spacing="-0.3" fill="#1E1B3A">Viveka</text><circle cx="142" cy="30" r="2.4" fill="#FBBF24"/></svg>`;
const verificationLogo = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(VERIFICATION_LOGO_SVG);

// Teal lockup (dark text on white), same treatment as the coach logo above but
// in the marketplace/participant brand colors — participants (non-providers).
const VERIFICATION_LOGO_SVG_PARTICIPANT = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 48" width="180" height="48"><defs><radialGradient id="vlgp2" cx="78%" cy="22%" r="65%"><stop offset="0%" stop-color="#5EEAD4"/><stop offset="50%" stop-color="#14B8A6"/><stop offset="100%" stop-color="#064E3B"/></radialGradient></defs><svg x="4" y="4" width="40" height="40" viewBox="0 0 120 120"><circle cx="60" cy="60" r="56" fill="url(#vlgp2)"/><path d="M26,26 L43,30 L58,78 L77,30 L96,24 L74,99 L48,99 Z" fill="white"/></svg><text x="56" y="32" font-family="Plus Jakarta Sans,sans-serif" font-weight="800" font-size="22.4" letter-spacing="-0.3" fill="#064E3B">Viveka</text><circle cx="142" cy="30" r="2.4" fill="#FBBF24"/></svg>`;
const verificationLogoParticipant = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(VERIFICATION_LOGO_SVG_PARTICIPANT);

/**
  Parse verification token from URL

  Returns stringified token, if the token is provided.

  Returns `null` if verification token is not provided.

  Please note that we need to explicitely stringify the token, because
  the unwanted result of the `parse` method is that it automatically
  parses the token to number.
*/
const parseVerificationToken = search => {
    const urlParams = parse(search);
    const verificationToken = urlParams.t;

    if (verificationToken) {
          return `${verificationToken}`;
    }

    return null;
};

export const EmailVerificationPageComponent = props => {
    const {
          currentUser,
          submitVerification,
          emailVerificationInProgress,
          isVerified,
          verificationError,
          location,
    } = props;
    // const title = intl.formatMessage({
    //   id: 'EmailVerificationPage.title',
    // });

    // FIX 1: Moved user declaration BEFORE useEffect to avoid TDZ ReferenceError
    const user = ensureCurrentUser(currentUser);

    // Coaches (isProvider) land on their coach dashboard; everyone else is a
    // participant and lands on the participant dashboard. Same isProvider
    // flag MainDashboardWrapper uses to pick coach vs. participant sidebar.
    const isProvider = !!user?.attributes?.profile?.publicData?.isProvider;
    const continueDestination = isProvider ? 'CoachDashboardPage' : 'SaaSClientDashboard';

    const initialValues = {
          verificationToken: parseVerificationToken(location ? location.search : null),
    };

    useEffect(() => {
          // Fire verification whenever a token is present in the URL and the current user is loaded.
          // Previously the guard `emailVerified === false` blocked email-change verification, since
          // emailVerified stays `true` for the old email — only the new pending email needs verifying.
          if (user && user.id && initialValues.verificationToken) {
            submitVerification({ verificationToken: initialValues.verificationToken });
          }
          // Re-run when the user becomes available (currentUser may still be loading on mount,
          // e.g. right after a login redirect).
    }, [user.id?.uuid])

    // Only short-circuit to LandingPage when there's no verification token to process.
    // For email-change flows the user already has emailVerified=true (for the old email),
    // so the previous `if (emailVerified) redirect` ran on mount and skipped verification entirely.
    if (user && user.id && user.attributes.emailVerified && !initialValues.verificationToken) {
          return <NamedRedirect name="LandingPage" />;
    }

    return (
          <PageWrapper>
            <ContainerWrapper>
              <div className={classNames(css.outerbase, !isProvider && css.outerbaseParticipant)}>
            <div className={classNames(css.base, !isProvider && css.baseParticipant)}>
            <div className={css.leftDiv}>
              <div className={css.logo}>
                <img alt="Viveka" src={isProvider ? verificationLogo : verificationLogoParticipant} />
  </div>
              <div className={css.verificationEmail}>
                {emailVerificationInProgress ? (
                  <>
                    <div className={classNames(css.spinner, !isProvider && css.spinnerParticipant)} aria-hidden="true" />
                    <p className={classNames(css.verificationTitle, !isProvider && css.verificationTitleParticipant)}>Verifying your email...</p>
                    <p className={css.verificationBody}>This will just take a moment.</p>
                  </>
                ) : isVerified ? (
                  <>
                    <div className={classNames(css.statusIconSuccess, !isProvider && css.statusIconSuccessParticipant)} aria-hidden="true">✓</div>
                    <p className={classNames(css.verificationTitle, !isProvider && css.verificationTitleParticipant)}>Email verified!</p>
                    <p className={css.verificationBody}>
                      Your email is now <strong>{user && user.attributes.email}</strong>.
                    </p>
                    <NamedLink name={continueDestination} className={classNames(css.continueButton, !isProvider && css.continueButtonParticipant)}>
                      Continue to Viveka
                    </NamedLink>
                  </>
                ) : verificationError ? (
                  <>
                    <div className={css.statusIconError} aria-hidden="true">!</div>
                    <p className={classNames(css.verificationTitle, !isProvider && css.verificationTitleParticipant)}>Verification failed</p>
                    <p className={css.verificationBody}>
                      The link may be expired, already used, or for a different account.
                      Try logging in with the email you wanted to change, then click the most
                      recent verification link.
                    </p>
                    <NamedLink name="LoginPage" className={classNames(css.continueButton, !isProvider && css.continueButtonParticipant)}>
                      Log in
                    </NamedLink>
                  </>
                ) : (
                  <>
                    <p className={classNames(css.verificationTitle, !isProvider && css.verificationTitleParticipant)}>Verify your email</p>
                    <p className={css.verificationBody}>
                      We have sent you a verification email to <strong>{user && user.attributes.email}</strong>.
                    </p>
                  </>
                )}
              </div>
</div>
            <div className={classNames(css.rightDiv, !isProvider && css.rightDivParticipant)}>
              <div>
                {isProvider ? (
                  <>
                    <p>Focus on transformation.</p>
                    <p>Expand your impact.</p>
                    <p>We run the infrastructure.</p>
                  </>
                ) : (
                  <>
                    <p>Focus on your growth.</p>
                    <p>Expand what's possible.</p>
                    <p>Turn insight into momentum.</p>
                  </>
                )}
  </div>
  </div>
  </div>
  </div>
  </ContainerWrapper>

      <ContainerWrapper>
          <Footer />
  </ContainerWrapper>
  </PageWrapper>
  );
};

EmailVerificationPageComponent.defaultProps = {
    currentUser: null,
    verificationError: null,
};

const { bool, func, shape, string } = PropTypes;

EmailVerificationPageComponent.propTypes = {
    currentUser: propTypes.currentUser,
    scrollingDisabled: bool.isRequired,
    submitVerification: func.isRequired,
    emailVerificationInProgress: bool.isRequired,
    verificationError: propTypes.error,

    // from withRouter
    location: shape({
          search: string,
    }).isRequired,

    // from injectIntl
    intl: intlShape.isRequired,
};

const mapStateToProps = state => {
    const { currentUser } = state.user;
    const { verificationError, verificationInProgress, isVerified } = state.EmailVerification;
    return {
          verificationError,
          emailVerificationInProgress: verificationInProgress,
          isVerified,
          currentUser,
          scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    submitVerification: ({ verificationToken }) => {
          return dispatch(verify(verificationToken));
    },
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const EmailVerificationPage = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
  )(EmailVerificationPageComponent);

EmailVerificationPage.loadData = (params, search) => {
    const token = parseVerificationToken(search);
    return verify(token);
};

export default EmailVerificationPage;
